/**
 * GCLID capture — keeps the Google Ads click ID from the landing URL and saves
 * it on the cart as the attribute "gclid", so it reaches the order as a custom
 * attribute (Order → Additional details).
 *
 * - Runs on every page. When the URL has ?gclid=…, the value is stored in
 *   localStorage for 90 days (Google Ads' longest click-through window), so a
 *   shopper who lands from an ad and buys later in the visit — or days later on
 *   the same browser — still carries it.
 * - The stored value is written to the cart with /cart/update.js only when the
 *   cart does not already hold that exact value (passed in from Liquid), and is
 *   also attached as attributes[gclid] when a cart form is submitted to
 *   checkout, which covers carts created without a page reload.
 * - Captures on every visit without waiting for cookie consent (merchant
 *   decision), so every order from an ad click carries the value.
 *
 * Script dataset (on the <script> tag):
 *   data-cart-gclid       — The cart's current "gclid" attribute (may be empty).
 *   data-cart-update-url  — routes.cart_update_url + ".js".
 */
(() => {
  const script = document.currentScript;
  const ATTRIBUTE = 'gclid';
  const STORAGE_KEY = 'pime_gclid';
  const PENDING_KEY = 'pime_gclid_pending';
  const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

  const cartValue = script?.dataset.cartGclid || '';
  const updateUrl = script?.dataset.cartUpdateUrl || '/cart/update.js';

  const fromUrl = (() => {
    try {
      const value = new URLSearchParams(window.location.search).get('gclid');
      return value ? value.trim().slice(0, 255) : '';
    } catch {
      return '';
    }
  })();

  const storage = {
    get(store, key) {
      try { return window[store].getItem(key); } catch { return null; }
    },
    set(store, key, value) {
      try { window[store].setItem(key, value); } catch { /* storage unavailable */ }
    },
    remove(store, key) {
      try { window[store].removeItem(key); } catch { /* storage unavailable */ }
    },
  };

  // Hold a fresh click ID for this tab until consent is known.
  if (fromUrl) storage.set('sessionStorage', PENDING_KEY, fromUrl);

  function storedValue() {
    const raw = storage.get('localStorage', STORAGE_KEY);
    if (!raw) return '';
    try {
      const { value, ts } = JSON.parse(raw);
      if (!value || Date.now() - ts > MAX_AGE_MS) {
        storage.remove('localStorage', STORAGE_KEY);
        return '';
      }
      return value;
    } catch {
      storage.remove('localStorage', STORAGE_KEY);
      return '';
    }
  }

  let synced = false;
  async function syncToCart(value) {
    if (!value || value === cartValue || synced) return;
    synced = true;
    try {
      await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [ATTRIBUTE]: value } }),
        keepalive: true,
      });
    } catch {
      synced = false;
    }
  }

  function process() {
    const pending = storage.get('sessionStorage', PENDING_KEY);
    if (pending) {
      storage.set('localStorage', STORAGE_KEY, JSON.stringify({ value: pending, ts: Date.now() }));
      storage.remove('sessionStorage', PENDING_KEY);
    }
    syncToCart(storedValue());
  }

  // Merchant decision (Oct 2026): capture the click ID on every visit without
  // waiting for cookie consent, so Google Ads offline conversions always have it.
  function run() {
    process();
  }

  // Products are added without a page reload, so a shopper can reach checkout
  // from the cart drawer before any page load syncs the attribute. The cart
  // forms submit natively to /cart, which saves attributes[...] fields before
  // redirecting to checkout — so attach the value at the moment of checkout.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    const isCheckout = submitter?.getAttribute('name') === 'checkout' || form.querySelector('[name="checkout"]');
    if (!isCheckout) return;

    process();
    const value = storedValue();
    if (!value) return;

    let field = form.querySelector(`input[name="attributes[${ATTRIBUTE}]"]`);
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = `attributes[${ATTRIBUTE}]`;
      form.append(field);
    }
    field.value = value;
  }, true);

  run();
  window.addEventListener('load', () => { if (!synced) run(); }, { once: true });
})();
